import React, { useState, KeyboardEvent, ChangeEvent, FocusEvent } from 'react';
import cn from 'classnames';
import { X } from 'src/common/icons';
import styles from '../styles.module.scss';

const DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const MAX_DOMAINS = 50;

export function normaliseDomain(raw: string): string {
  let v = raw.trim().toLowerCase();
  v = v.replace(/^@+/, '');
  v = v.replace(/^https?:\/\//, '');
  v = v.replace(/\/+$/, '');
  return v;
}

export function isValidDomain(value: string): boolean {
  return DOMAIN_RE.test(value);
}

type DomainChipInputProps = {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  max?: number;
};

export const DomainChipInput: React.FC<DomainChipInputProps> = ({
  value,
  onChange,
  disabled = false,
  max = MAX_DOMAINS,
}) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const atLimit = value.length >= max;

  const commit = (raw: string): boolean => {
    const normalised = normaliseDomain(raw);
    if (!normalised) {
      setDraft('');
      setError(null);
      return true;
    }
    if (!isValidDomain(normalised)) {
      setError('Enter a valid domain, e.g. example.com');
      return false;
    }
    if (value.some((d) => d.toLowerCase() === normalised)) {
      setDraft('');
      setError(null);
      return true;
    }
    if (value.length >= max) {
      setError(`Maximum of ${max} domains reached`);
      return false;
    }
    onChange([...value, normalised]);
    setDraft('');
    setError(null);
    return true;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (next.includes(',')) {
      const parts = next.split(',');
      const last = parts.pop() ?? '';
      for (const part of parts) {
        if (!commit(part)) {
          setDraft(last);
          return;
        }
      }
      setDraft(last);
      return;
    }
    setDraft(next);
    if (error) setError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (draft.trim()) {
        const ok = commit(draft);
        if (ok) {
          e.preventDefault();
        } else if (e.key === 'Tab') {
          e.preventDefault();
        }
      }
      return;
    }
    if (e.key === 'Backspace' && draft === '' && value.length > 0 && !disabled) {
      onChange(value.slice(0, -1));
    }
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    if (draft.trim()) {
      commit(draft);
    }
  };

  const handleRemove = (domain: string) => {
    onChange(value.filter((d) => d !== domain));
    if (error) setError(null);
  };

  return (
    <div
      className={cn(styles.chipEditor, {
        [styles.chipEditorDisabled]: disabled,
      })}
    >
      <div className={styles.chipList}>
        {value.map((domain) => (
          <span key={domain} className={styles.chip}>
            <span className={styles.chipText}>{domain}</span>
            {!disabled && (
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${domain}`}
                onClick={() => handleRemove(domain)}
              >
                <X />
              </button>
            )}
          </span>
        ))}
        <input
          type="text"
          className={styles.chipInput}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={
            disabled
              ? ''
              : atLimit
              ? 'Limit reached'
              : value.length === 0
              ? 'e.g. client.com'
              : 'Add another domain'
          }
          disabled={disabled || atLimit}
          aria-label="Add allowed notification domain"
        />
      </div>
      <div className={styles.chipMeta}>
        <span
          className={cn(styles.chipCounter, {
            [styles.chipCounterFull]: atLimit,
          })}
        >
          {value.length} / {max}
        </span>
        {error && !disabled && <span className={styles.chipError}>{error}</span>}
      </div>
    </div>
  );
};

export default DomainChipInput;
